import { useCallback, useEffect } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';
import type { DeliveryOrderDetail } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

import {
  SUCCESS_COMPLETE,
  sumLedgerTotal,
  useOrdering,
} from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type PartialLineItem } from '../../../../graphql';
import { useNotificationType } from '../../../../integrations';
import { getDeliveryParams, getTips } from '../../utils';
import { useAddUpsellToBag } from '../useAddUpsellToBag';
import { useBagCancelPreviousIntent } from '../useBagCancelPreviousIntent';
import { useBagLoyaltyData } from '../useBagLoyaltyData';
import { useBagLoyaltyRewards } from '../useBagLoyaltyRewards';
import { useBagPaymentInfo } from '../useBagPaymentInfo';
import { useBagTracking } from '../useBagTracking';
import { useCart } from '../useCart';
import { useChangeChannelOrLocation } from '../useChangeChannelOrLocation';
import { useCreatePaymentIntent } from '../useCreatePaymentIntent';
import { useCredits } from '../useCredits';
import { useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { useFetchOrderPaymentState } from '../useFetchOrderPaymentState';
import { useFetchTelemetryData } from '../useFetchTelemetryData';
import { useFetchWantedTimes } from '../useFetchWantedTimes';
import { useGiftCards } from '../useGiftCards';
import { useNavigatePostOrder } from '../useNavigatePostOrder';
import { usePaymentMethods } from '../usePaymentMethods';
import { usePostOrderAppReview } from '../usePostOrderAppReview';
import { usePostOrderFetch } from '../usePostOrderFetch';
import { usePostOrderHistory } from '../usePostOrderHistory';
import { usePostOrderNotifications } from '../usePostOrderNotifications';
import { usePostOrderPurchaseModal } from '../usePostOrderPurchaseModal';
import { usePostOrderTracking } from '../usePostOrderTracking';
import { usePromoCode } from '../usePromoCode';
import { useRequestLogin } from '../useRequestLogin';
import { useRewards } from '../useRewards';
import { useSubmitOrder } from '../useSubmitOrder';
import { useUpdateLineItem } from '../useUpdateLineItem';

/**
 * Hook that binds the ordering machine with the external data.
 */
export const useOrderState = (params: UseOrderStateParams) => {
  const { orderSubmissionDelay } = params;

  const { track } = useTelemetry();
  const isLoggedIn = useIsLoggedIn();
  const bagTracking = useBagTracking();
  const dynamicTips = useFeatureFlag('CELS-1191-permanent-dynamic-tip-values');
  const wantedTimesPollingInterval = useFeatureFlag('permanent-bag-wanted-times-polling-interval-milliseconds'); // prettier-ignore
  const isIntentConfirmationEnabled = useFeatureFlag('CELS-4725-stripe-client-intent-confirmation-enabled'); // prettier-ignore

  // ─── Loyalty ──────────────────────────────────────────────────────────────

  const { fetchLoyaltyData } = useBagLoyaltyData();
  const { fetchRewards } = useBagLoyaltyRewards();
  const { applyReward, removeReward } = useRewards();

  // ──────────────────────────────────────────────────────────────────────────

  const { fetchCart } = useCart();
  const { fetchCredits } = useCredits();
  const { fetchWantedTimes } = useFetchWantedTimes();
  const { addUpsellToBag } = useAddUpsellToBag();
  const { fetchGiftCardBalance, applyGiftCard } = useGiftCards(fetchCredits);
  const { fetchBagPaymentInfo } = useBagPaymentInfo();
  const { fetchPaymentMethods } = usePaymentMethods();
  const { fetchDefaultPaymentMethod, setDefaultPaymentMethod } =
    useDefaultPaymentMethod();
  const { updateLineItem } = useUpdateLineItem();
  const { applyPromoCode } = usePromoCode(fetchRewards);
  const { changeChannelOrLocation } = useChangeChannelOrLocation();
  const { requestLogin } = useRequestLogin();
  const { submitOrder } = useSubmitOrder(setDefaultPaymentMethod);
  const { fetchTelemetryData } = useFetchTelemetryData();
  const { fetchOrderPaymentState } = useFetchOrderPaymentState();
  const { fetchStatePostOrder } = usePostOrderFetch();
  const { navigatePostOrder } = useNavigatePostOrder();
  const { cancelPreviousIntent } = useBagCancelPreviousIntent();
  const { handlePostOrderPurchaseModal } = usePostOrderPurchaseModal();
  const { handlePostOrderNotifications } = usePostOrderNotifications();
  const { handlePostOrderTracking } = usePostOrderTracking();
  const { handlePostOrderHistory } = usePostOrderHistory();
  const { handlePostOrderAppReview } = usePostOrderAppReview();

  const fetchInitialData = useCallback(async () => {
    if (!isLoggedIn) {
      return {
        cart: await fetchCart(),
        paymentMethods: [],
        defaultPaymentMethodId: undefined,
        defaultTip: 0,
        availablePoints: 0,
        availableCredit: 0,
        giftCardBalance: 0,
        selectedTip: 0,
        availableTips: [],
        customerId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        stripeCustomerId: '',
        stripeCustomerKey: '',
        stripeSession: '',
        isNewCustomer: true,
        isOptedOutFromLoyalty: false,
      };
    }

    const [
      cart,
      paymentMethods,
      defaultPaymentMethodId,
      availableCredit,
      giftCardBalance,
      telemetryData,
      loyaltyData,
      bagPaymentInfo,
    ] = await Promise.all([
      fetchCart(),
      fetchPaymentMethods(),
      fetchDefaultPaymentMethod(),
      fetchCredits(),
      fetchGiftCardBalance(),
      fetchTelemetryData(),
      fetchLoyaltyData(),
      fetchBagPaymentInfo(),
    ]);

    const tips = getTips({
      maxTipAmount: cart.maxTipAmount,
      orderChannel: cart.orderChannel,
      dynamicTips,
    });

    return {
      cart,
      paymentMethods,
      defaultPaymentMethodId,
      availableCredit,
      giftCardBalance,
      ...tips,
      ...bagPaymentInfo,
      ...loyaltyData,
      ...telemetryData,
    };
  }, [
    isLoggedIn,
    dynamicTips,
    fetchCart,
    fetchPaymentMethods,
    fetchDefaultPaymentMethod,
    fetchCredits,
    fetchGiftCardBalance,
    fetchTelemetryData,
    fetchLoyaltyData,
    fetchBagPaymentInfo,
  ]);

  const response = useOrdering<PartialLineItem, DeliveryOrderDetail>({
    isLoggedIn,
    orderSubmissionDelay,
    isIntentConfirmationEnabled,
    wantedTimesPollingInterval,
    bagTracking,
    navigatePostOrder,
    fetchInitialData,
    fetchRewards,
    fetchWantedTimes,
    fetchOrderPaymentState,
    fetchStatePostOrder,
    addUpsellToBag,
    updateLineItem,
    changeChannelOrLocation,
    requestLogin,
    applyReward,
    removeReward,
    applyPromoCode,
    applyGiftCard,
    cancelPreviousIntent,
    submitOrder,
    handlePostOrderPurchaseModal,
    handlePostOrderNotifications,
    handlePostOrderTracking,
    handlePostOrderHistory,
    handlePostOrderAppReview,
  });

  // ─── Stripe ──────────────────────────────────────────────────────────
  // The Stripe Confirm Handler requires a ref otherwise either the context
  // is stale or the SDK re-renders on every change to the properties below.

  const {
    wantedTime,
    selectedTip,
    cart,
    availableCredit,
    shouldUseCredit,
    dropoffLocation,
    shouldIncludeUtensils,
    registerStripeConfirmHandler,
    requestOrderPlayback,
    changePaymentMethod,
    cancelRequest,
  } = response;

  const getNotificationType = useNotificationType();
  const createPaymentIntent = useCreatePaymentIntent();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  useEffect(() => {
    registerStripeConfirmHandler(
      async (paymentResult, _shouldSave, intentCreationCallback) => {
        try {
          if (!isIntentConfirmationEnabled) {
            track('checkout.stripe-usage', { event: 'submit_payment_method' });
            intentCreationCallback(SUCCESS_COMPLETE);
            changePaymentMethod(paymentResult.id);
            requestOrderPlayback(paymentResult.id);
            return;
          }

          const {
            orderId = '',
            orderChannel = 'pickup',
            restaurantId = '',
            deliveryOrderDetail,
            canTrackOrderStatus,
            ledger,
          } = cart;

          const paymentMethodId = paymentResult.id;
          const paymentMethodType = paymentResult.paymentMethodType;
          const wantedTimeString = wantedTime?.time ?? '';
          const deliveryParams = getDeliveryParams(orderChannel, selectedTip, deliveryOrderDetail); // prettier-ignore
          const notificationType = getNotificationType({ canTrackOrderStatus, wantedTime: wantedTimeString }).notificationType ?? ''; // prettier-ignore
          const amount = sumLedgerTotal(ledger, selectedTip, availableCredit, shouldUseCredit).total; // prettier-ignore

          const params = {
            paymentMethodId,
            amount,
            orderId,
            orderType: OrderTypeValues.GravyOrder,
            restaurantEntityId: restaurantId,
            deliveryOrderDetail: deliveryParams,
            dropoffLocationId: dropoffLocation,
            includeUtensils: shouldIncludeUtensils,
            notificationType,
            pickupTip: selectedTip,
            useCredit: shouldUseCredit,
            wantedTime: wantedTimeString,
          };

          track('checkout.stripe-usage', { event: 'create_payment_intent', paymentMethodType, params }); // prettier-ignore

          const response = await createPaymentIntent(params);
          const { paymentIntentId, clientSecret } = response;

          changePaymentMethod(paymentIntentId);

          track('checkout.stripe-usage', { event: 'confirm_payment_intent', paymentIntentId }); // prettier-ignore
          intentCreationCallback({ clientSecret });
        } catch (error) {
          cancelRequest(true);
          track('checkout.stripe-usage', { event: 'stripe_failure', error }); // prettier-ignore
          addNoticeBanner({
            text: (error as Error).message,
            palette: 'caution',
          });
        }
      },
    );
  }, [
    addNoticeBanner,
    availableCredit,
    cancelRequest,
    cart,
    changePaymentMethod,
    createPaymentIntent,
    dropoffLocation,
    getNotificationType,
    isIntentConfirmationEnabled,
    registerStripeConfirmHandler,
    requestOrderPlayback,
    response,
    selectedTip,
    shouldIncludeUtensils,
    shouldUseCredit,
    track,
    wantedTime?.time,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return response;
};

type UseOrderStateParams = {
  orderSubmissionDelay: number;
};
